import React, { CSSProperties, FC, useEffect, useRef } from "react";
import cls from "./timeline.module.scss";

interface IProps {
    item: {
        customClassNames: string;
        timelineSettings: {
            points: Array<{
                icon: string;
                heading: string;
                content: string;
                iconSize: string,
                headingPosition: string;
            }>
            settings: {
                iconColor: string,
                lineColor: string,
                dotsColor: string,
                headingColor: string
            }
        }
        convertedSettings: {
            styles: CSSProperties;
            parentStyles: CSSProperties
        }
    }
}

const TimelineChart: FC<IProps> = (
    {
        item: {
            customClassNames,
            timelineSettings: {
                points,
                settings: {
                    iconColor,
                    lineColor,
                    dotsColor,
                    headingColor
                }
            },
            convertedSettings: { styles, parentStyles }
        }
    }
) => {
    const chartWidthHandle = () => {
        const margin = (styles?.margin as string) || "0px";
        const splittedMargins = margin.split(" ");
        const [top = "0px", right = "0px", bottom = "0px", left = "0px"] = splittedMargins;
        const isZeroMargin = [top, right, bottom, left].every(val => val === "0px");
        return isZeroMargin ? "100%" : `calc(100% - ${right} - ${left})`;
    }
    const timelineLines = Array.from({ length: 10 }, (_, index) => (
        <span key={index} style={{background: lineColor}} className={cls.timeline__line}></span>
    ));

    const startX = useRef(0);
    const scrollLeft = useRef(0);
    const [dragging, setDragging] = React.useState(false);
    const isDragging = useRef(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const handleWheel = (e: WheelEvent) => {
            const el = scrollRef.current;
            if (!el) return;

            const isScrollable = el.scrollWidth > el.clientWidth;

            if (!isScrollable) return;

            const rect = el.getBoundingClientRect();
            const inView = rect.top < window.innerHeight && rect.bottom > 0;

            if (inView && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                el.scrollLeft = el.scrollLeft + e.deltaY;
            }
        };


        el.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            el.removeEventListener("wheel", handleWheel);
        };
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        startX.current = e.pageX - (scrollRef.current?.offsetLeft || 0);
        scrollLeft.current = scrollRef.current?.scrollLeft || 0;
        setDragging(true);
    };

    const handleMouseLeave = () => {
        isDragging.current = false;
        setDragging(false);
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        setDragging(false);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging.current) return;
        e.preventDefault();
        const x = e.pageX - (scrollRef.current?.offsetLeft || 0);
        const walk = (x - startX.current);
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollLeft.current - walk;
        }
    };



    return (
        <div style={parentStyles} className={customClassNames}>
            <div
                ref={scrollRef}
                className={cls["timeline-chart"]}
                style={{
                    ...styles,
                    width: chartWidthHandle(),
                    cursor: dragging ? "grabbing" : "grab"
                }}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
            >
                <div className={cls.first_and_last_timeline}>
                    <div className={cls.timeline_heading} style={{color: headingColor}}>
                        {points[0].heading}</div>
                    <div className={cls.timeline_content}>
                        <div className={cls.text} dangerouslySetInnerHTML={{__html: points[0].content}}/>
                    </div>
                </div>
                <div className={cls["timeline-chart-inner"]}>
                    {points.map(({ icon, heading, content, iconSize, headingPosition }, index) => {
                        let headingStyles;
                        let contentStyles;
                        const isFirstPoint = index === 0;
                        const isLastPoint = index === points.length - 1;
                        const iconStyles = {color: iconColor, width: `${iconSize}px`, height: `${iconSize}px`};

                        if (headingPosition === "bottom") {
                            contentStyles = {bottom: "80px"};
                            headingStyles = {top: "80px", color: headingColor};
                        } else {
                            contentStyles = {top: "80px"};
                            headingStyles = {bottom: "80px", color: headingColor};
                        }

                        return (
                            <div key={index}
                                 className={`${cls["timeline-point"]} ${isFirstPoint ? cls.first__point : ""} ${isLastPoint ? cls.last__point : ""}`}
                            >
                                {!isFirstPoint && !isLastPoint && (
                                    <div className={cls["timeline-label"]}>
                                        <div className={cls.timeline_heading}
                                             style={headingStyles}>
                                            {heading}</div>
                                        <div className={cls.timeline_content}
                                             style={contentStyles}>
                                            {icon &&
                                                <div className={cls.icon_container} style={{borderColor: dotsColor}}>
                                                    <div style={iconStyles} className={`${cls.point_icon}`}
                                                         dangerouslySetInnerHTML={{__html: icon}}/>
                                                </div>}
                                            <div className={cls.text}
                                                 dangerouslySetInnerHTML={{__html: content}}/>
                                        </div>
                                    </div>
                                )}
                                <div className={cls.lines}>
                                    {isFirstPoint &&
                                        <span style={{background: dotsColor}} className={cls.point__line}></span>
                                    }
                                    {!isLastPoint && (
                                        <>
                                            <div style={{background: lineColor}} className={cls.timeline_lines}>
                                                {timelineLines}
                                            </div>
                                            <span style={{background: dotsColor}} className={cls.point__line}></span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
                <div className={cls.first_and_last_timeline}>
                    <div className={cls.timeline_heading} style={{color: headingColor}}>
                        {points[points.length - 1].heading}</div>
                    <div className={cls.timeline_content}>
                        <div className={cls.text} dangerouslySetInnerHTML={{__html: points[points.length - 1].content}}/>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TimelineChart;
